import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  AdminUser,
  UsersFilters,
  listUsers,
  createUser as apiCreateUser,
  updateUser as apiUpdateUser,
  deleteUser as apiDeleteUser,
  changeUserStatus as apiChangeUserStatus,
  UserStatus,
  UpdateUserRequest,
  CreateUserRequest,
} from '@/admin/api/users';
import type { Pagination } from '@/admin/api/courts';

export interface AdminUsersState {
  items: AdminUser[];
  pagination: { page: number; pageSize: number; total?: number; pageCount?: number };
  filters: UsersFilters;
  loading: boolean;
  error?: string;
}

const initialState: AdminUsersState = {
  items: [],
  pagination: { page: 1, pageSize: 10, total: 0, pageCount: 1 },
  filters: { page: 1, pageSize: 10 },
  loading: false,
};

export const fetchUsers = createAsyncThunk<
  Pagination<AdminUser>,
  void
>('adminUsers/fetchUsers', async (_arg, thunkApi) => {
  const state = thunkApi.getState() as any;
  const { filters } = state.adminUsers as AdminUsersState;
  const resp = await listUsers(filters);
  return resp;
});

export const createUserThunk = createAsyncThunk<AdminUser, { payload: CreateUserRequest }>(
  'adminUsers/create',
  async ({ payload }) => {
    return await apiCreateUser(payload);
  }
);

export const updateUserThunk = createAsyncThunk<AdminUser, { id: number; payload: UpdateUserRequest }>(
  'adminUsers/update',
  async ({ id, payload }) => {
    return await apiUpdateUser(id, payload);
  }
);

export const deleteUserThunk = createAsyncThunk<void, { id: number }>(
  'adminUsers/delete',
  async ({ id }) => {
    await apiDeleteUser(id);
  }
);

export const changeUserStatusThunk = createAsyncThunk<
  AdminUser,
  { id: number; status: UserStatus; message?: string }
>('adminUsers/changeStatus', async ({ id, status, message }) => {
  return await apiChangeUserStatus(id, status, message);
});

const adminUsersSlice = createSlice({
  name: 'adminUsers',
  initialState,
  reducers: {
    setFilters(state, action: PayloadAction<Partial<UsersFilters>>) {
      state.filters = { ...state.filters, ...action.payload, page: 1 };
    },
    setPage(state, action: PayloadAction<number>) {
      state.filters.page = action.payload;
    },
    setPageSize(state, action: PayloadAction<number>) {
      state.filters.pageSize = action.payload;
      state.filters.page = 1;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items;
        state.pagination = {
          page: action.payload.page,
          pageSize: action.payload.pageSize,
          total: action.payload.total,
          pageCount: action.payload.pageCount,
        };
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to load users';
      })
      .addCase(createUserThunk.fulfilled, (state, action) => {
        state.items = [action.payload, ...state.items];
        if (typeof state.pagination.total === 'number') {
          state.pagination.total += 1;
        }
      })
      .addCase(updateUserThunk.fulfilled, (state, action) => {
        const updated = action.payload;
        const idx = state.items.findIndex((u) => u.id === updated.id);
        if (idx >= 0) state.items[idx] = { ...state.items[idx], ...updated };
      })
      .addCase(deleteUserThunk.fulfilled, (state, action) => {
        const id = (action.meta.arg as { id: number }).id;
        state.items = state.items.filter((u) => u.id !== id);
        if (typeof state.pagination.total === 'number' && state.pagination.total > 0) {
          state.pagination.total -= 1;
        }
      })
      .addCase(changeUserStatusThunk.fulfilled, (state, action) => {
        const updated = action.payload;
        const idx = state.items.findIndex((u) => u.id === updated.id);
        if (idx >= 0) state.items[idx] = { ...state.items[idx], ...updated };
      });
  },
});

export const { setFilters, setPage, setPageSize } = adminUsersSlice.actions;
export default adminUsersSlice;
